/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useDispatch } from "react-redux";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import CardContent from "@material-ui/core/CardContent";

import {
  SimpleShowLayout,
  WithReduxForm,
  PageHeader,
  TextField,
  FunctionField,
  TextInput,
  BooleanInput,
  showNotification,
  PageFooter,
  BackButton,
  Button,
  required as jsRequired
} from "jazasoft";
import { required } from "@jazasoft/mui-table";

// material-ui
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import { Divider, Grid } from "@material-ui/core";

// helper
import useQuery from "../../../hooks/useQuery";
import { isEmpty, isTrue } from "../../../utils/helpers";
import useSafeState from "../../../hooks/useSafeState";
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";
import useMutation from "../../../hooks/useMutation";
import useDeepMemo from "../../../hooks/useDeepMemo";

const useStyles = makeStyles(() => ({
  content: {
    margin: "1.5em"
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.75em"
  },
  mrTop: {
    marginTop: "1.5em"
  }
}));

const StyleView = props => {
  const classes = useStyles();
  const { id, i18nKey, resource } = props;

  const [version, setVersion] = useSafeState(1);
  const [editing, setEditing] = useSafeState(false);

  // hooks
  const dispatch = useDispatch();
  const [mutate, { loading }] = useMutation();

  const { data: apiList = [] } = useQuery({ url: "apiKeys/apis" });
  const { data: apiKey } = useQuery({ url: `apiKeys/${id}`, version });

  const onSubmit = handleSubmit => {
    handleSubmit(values => {
      const data = { ...apiKey, ...values };

      let options = {
        url: `${resource}/${id}`,
        method: "put",
        data
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("ApiKey details updated successfully."));
          setEditing(false);
          setVersion(version => version + 1);
        }
      });
    })();
  };

  const onApiSubmit = (values, _, onSubmitComplete) => {
    const urlScopeMap = values.reduce(
      (acc, e) => ({
        ...acc,
        [`${e.resourceId},${e.url}`]: acc[`${e.resourceId},${e.url}`] ? [...acc[`${e.resourceId},${e.url}`], e.scopeAction] : [e.scopeAction]
      }),
      {}
    );
    const apis = Object.keys(urlScopeMap).map(key => ({
      url: key.split(",")[1],
      resourceId: key.split(",")[0],
      scopeActionList: urlScopeMap[key]
    }));

    const data = { ...apiKey, apis: JSON.stringify(apis) };

    let options = {
      url: `${resource}/${id}`,
      method: "put",
      data
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("API details updated successfully."));
        setVersion(version => version + 1);
        onSubmitComplete && onSubmitComplete();
      }
    });
  };

  const columns = useDeepMemo(() => {
    let columnList = [
      {
        dataKey: "resourceId",
        title: "Resource",
        inputType: "select-input",
        choices: apiList.map(e => ({ id: e.resourceId, name: e.resourceId })),
        validate: [required()]
      },
      {
        dataKey: "url",
        title: "Url",
        inputType: "select-input",
        choices: ({ row }) => {
          const api = apiList.find(api => api.resourceId === row?.resourceId);
          return api?.urlList?.map(url => ({ id: url, name: url })) || [];
        },
        validate: [required()]
      },
      {
        dataKey: "scopeAction",
        title: "ScopeAction",
        inputType: "text-input"
      }
    ];
    return columnList;
  }, [apiKey, apiList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(apiKey?.apis)) return [];
    const rowList = JSON.parse(apiKey?.apis).flatMap(e => e.scopeActionList?.map(sa => ({ ...e, scopeAction: sa })));
    return rowList;
  }, [apiKey]);

  return (
    <div className={classes.root}>
      <PageHeader title={`Api Key #${id}`} />

      <div className={classes.content}>
        <WithReduxForm initialValues={apiKey}>
          {({ handleSubmit }) => (
            <Card className={classes.mrTop}>
              <CardHeader title="Basic Details" />
              <Divider />

              <CardContent>
                {editing && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <TextInput source="appId" i18nKey={i18nKey} validate={[jsRequired()]} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={3} style={{ display: "flex", alignItems: "center" }}>
                      <BooleanInput source="active" i18nKey={i18nKey} fullWidth />
                    </Grid>
                  </Grid>
                )}
                {!editing && (
                  <SimpleShowLayout style={{ paddingTop: "1em" }} record={apiKey} i18nKey={i18nKey} footer={false}>
                    <TextField label="App Id" source="appId" xs={12} sm={3} />
                    <TextField source="key" xs={12} sm={6} />
                    <FunctionField source="active" render={record => (isTrue(record?.active) ? "Yes" : "No")} xs={12} sm={3} />
                  </SimpleShowLayout>
                )}
              </CardContent>
              <Divider />
              <div className={classes.footer}>
                {!editing && <Button label="Edit" variant="outlined" color="primary" onClick={() => setEditing(true)} />}
                {editing && (
                  <Button label="Cancel" variant="outlined" color="primary" onClick={() => setEditing(false)} style={{ marginRight: "1em" }} />
                )}
                {editing && <Button label="Save" variant="contained" color="primary" disabled={loading} onClick={() => onSubmit(handleSubmit)} />}
              </div>
            </Card>
          )}
        </WithReduxForm>

        <Card className={classes.mrTop}>
          <CardHeader title="Operations" />
          <Divider />

          <PerfectScrollbar>
            <MuiTable
              columns={columns}
              rows={rows}
              editable
              enableRowAddition
              showEditableActions
              footerActions={[{ name: "edit", tooltip: "Edit", options: { variant: "outlined", color: "primary" } }]}
              rowAddCount={1}
              onRowAdd={(rows, rowIdx) => (!isEmpty(rowIdx) ? rows[rowIdx] : rows[rows?.length - 1])}
              onSubmit={onApiSubmit}
              fontSize={16}
            />
          </PerfectScrollbar>
        </Card>

        <PageFooter>
          <BackButton variant="outlined" color="primary" />
        </PageFooter>
      </div>
    </div>
  );
};

export default StyleView;
